import { useCallback, useRef, useState } from 'react';
import { useCommandEnter } from '../hooks/useCommandEnter';
import { useEscapeKey } from '../hooks/useEscapeKey';
import { cards as cardsApi, type CardAttachment } from '../lib/api';
import type { Column } from '../lib/api';

interface Props {
  onSubmit: (data: { title?: string; description: string; columnId?: string }) => Promise<{ id: string }>;
  onClose: () => void;
  boardId: string;
  defaultColumnId?: string;
  /** When provided and length > 1, a phase selector is shown. */
  creatableColumns?: Column[];
}

export function CreateCardModal({ onSubmit, onClose, boardId, defaultColumnId, creatableColumns }: Props) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [pendingImages, setPendingImages] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Determine the initially selected column
  const initialColumnId =
    defaultColumnId ??
    (creatableColumns && creatableColumns.length > 0 ? creatableColumns[0].id : undefined);

  const [selectedColumnId, setSelectedColumnId] = useState<string | undefined>(initialColumnId);

  useEscapeKey(onClose);

  // Require at least a title or description
  const canSubmit = (title.trim() || description.trim()) && !uploading;

  const doSubmit = useCallback(async () => {
    if (!canSubmit) return;
    setUploading(true);
    try {
      const result = await onSubmit({
        ...(title.trim() ? { title: title.trim() } : {}),
        description: description.trim(),
        ...(selectedColumnId ? { columnId: selectedColumnId } : {}),
      });

      const cardId = result.id;

      if (pendingImages.length > 0) {
        const now = new Date().toISOString();
        // Upload all images concurrently
        const uploadResults = await Promise.allSettled(
          pendingImages.map(async (file) => {
            const urlRes = await cardsApi.attachmentUploadUrl(boardId, cardId, {
              filename: file.name,
              contentType: file.type,
              size: file.size,
            });
            if (!urlRes.success || !urlRes.data) throw new Error('Failed to get upload URL');
            const { uploadUrl, s3Key, attachmentId, filename } = urlRes.data;

            await fetch(uploadUrl, {
              method: 'PUT',
              body: file,
              headers: { 'Content-Type': file.type },
            });

            return {
              id: attachmentId,
              s3Key,
              filename,
              contentType: file.type,
              size: file.size,
              uploadedAt: now,
              uploadedBy: '',
            } satisfies CardAttachment;
          })
        );

        const attachments: CardAttachment[] = uploadResults
          .filter((r): r is PromiseFulfilledResult<CardAttachment> => r.status === 'fulfilled')
          .map((r) => r.value);

        if (attachments.length > 0) {
          await cardsApi.update(boardId, cardId, {
            metadata: { attachments } as any,
          });
        }
      }
    } finally {
      setUploading(false);
      onClose();
    }
  }, [canSubmit, title, description, selectedColumnId, onSubmit, boardId, pendingImages, onClose]);

  useCommandEnter(doSubmit);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    doSubmit();
  };

  // Show phase selector only when multiple options exist and no column is locked
  const showPhaseSelector =
    !defaultColumnId && creatableColumns && creatableColumns.length > 1;

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center md:items-center" onClick={onClose}>
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
      <form
        onSubmit={handleSubmit}
        onClick={(e) => e.stopPropagation()}
        className="relative z-10 flex w-full flex-col rounded-t-2xl border border-gray-800 bg-surface-850 p-5 shadow-2xl md:max-w-md md:rounded-xl md:p-6"
      >
        <h2 className="mb-4 text-lg font-semibold">Create Card</h2>

        <div className="space-y-4">
          {showPhaseSelector && (
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-gray-500">
                Phase
              </label>
              <div className="flex flex-wrap gap-2">
                {creatableColumns!.map((col) => (
                  <button
                    key={col.id}
                    type="button"
                    onClick={() => setSelectedColumnId(col.id)}
                    className={`rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${
                      selectedColumnId === col.id
                        ? 'bg-primary-600 text-white'
                        : 'bg-gray-800 text-gray-400 hover:bg-gray-700 hover:text-gray-200'
                    }`}
                  >
                    {col.name}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-gray-500">
              Title <span className="normal-case font-normal text-gray-600">(optional)</span>
            </label>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full rounded-lg border border-gray-700 bg-surface-900 px-3 py-2.5 text-base text-gray-100 outline-none focus:border-primary-500 md:text-sm md:py-2"
              placeholder="What needs to be done?"
            />
          </div>

          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-gray-500">
              Description
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={5}
              className="w-full resize-none rounded-lg border border-gray-700 bg-surface-900 px-3 py-2.5 text-base text-gray-100 outline-none focus:border-primary-500 md:text-sm md:py-2"
              placeholder="Describe the task in detail…"
              autoFocus
            />
          </div>

          {/* Image attachments */}
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-gray-500">
              Images <span className="normal-case font-normal text-gray-600">(optional, max 10)</span>
            </label>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/gif,image/webp,image/heic"
              multiple
              className="sr-only"
              onChange={(e) => {
                const files = Array.from(e.target.files ?? []);
                setPendingImages((prev) => [...prev, ...files].slice(0, 10));
                // Reset input so same file can be re-selected
                e.target.value = '';
              }}
            />
            {pendingImages.length > 0 && (
              <div className="mb-2 flex flex-wrap gap-2">
                {pendingImages.map((f, i) => (
                  <div key={i} className="relative">
                    <img
                      src={URL.createObjectURL(f)}
                      alt={f.name}
                      className="h-16 w-16 rounded-lg border border-gray-700 object-cover"
                    />
                    <button
                      type="button"
                      onClick={() => setPendingImages((prev) => prev.filter((_, j) => j !== i))}
                      className="absolute -right-1 -top-1 flex h-4 w-4 items-center justify-center rounded-full bg-gray-800 text-xs text-gray-400 hover:text-white"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={pendingImages.length >= 10}
              className="flex items-center gap-1.5 rounded-lg border border-dashed border-gray-700 px-3 py-2 text-sm text-gray-400 hover:border-gray-500 hover:text-gray-300 disabled:opacity-40"
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 001.5-1.5V6a1.5 1.5 0 00-1.5-1.5H3.75A1.5 1.5 0 002.25 6v12a1.5 1.5 0 001.5 1.5zm10.5-11.25h.008v.008h-.008V8.25zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z" />
              </svg>
              Attach images
            </button>
          </div>
        </div>

        <div className="mt-5 flex gap-3 md:mt-6 md:justify-end">
          <button
            type="button"
            onClick={onClose}
            disabled={uploading}
            className="flex-1 rounded-lg px-4 py-2.5 text-sm font-medium text-gray-400 hover:bg-gray-800 hover:text-gray-200 disabled:opacity-40 md:flex-none md:py-2"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!canSubmit}
            className="flex-1 rounded-lg bg-primary-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-primary-500 disabled:opacity-40 md:flex-none md:py-2"
          >
            {uploading
              ? pendingImages.length > 0
                ? 'Uploading…'
                : 'Creating…'
              : 'Create Card'}
          </button>
        </div>
      </form>
    </div>
  );
}
